import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { StoreAplication } from "base/src/main";
import { LOADING_REMOVE, NETWORK, StateMensage, StateType } from '../../types/base/base';
import { DispatchLoading, DispatchType, isValidationResult } from '../../utils/ReactUtils';
import { _UserData, UserDataBaseEnum } from 'base/src/infra/storage/localStorage';
import { LOGIN_TOKEN } from '../../types/authentication/authentication';
import ValidationResult from '../../types/global/validationResult';
import { IsStorybook, IsDebug, IsTest, IsDevSimulate } from '../../utils/domainUtil';
import { ToastAwait } from 'base/src/utils/toastUtil';
import { EventSource } from 'eventsource'
import DomainConstants from './domainConstants';
import { UsuarioResponse } from '../../types/usuario/usuarioResponse';

function GetStorageToken(): string | undefined {
  const userData = _UserData;
  const token = userData.Get(UserDataBaseEnum.Token);
  return (token != null && token != "") ? `Bearer ${token}` : undefined
}

// JWT Refresh interceptor — catches 401s, refreshes token, retries request
let refreshPromise: Promise<UsuarioResponse | null> | null = null;

function isRefreshEndpoint(url: string | undefined): boolean {
  return url != null && url.includes('/authentication/Refresh');
}

async function doRefreshToken(): Promise<UsuarioResponse | null> {
  const refreshToken = _UserData.Get<string>(UserDataBaseEnum.RefreshToken);
  if (refreshToken == null || refreshToken === '') {
    return null;
  }

  try {
    const response = await axios.post(
      DomainConstants.apiDomain.authentication.refresh,
      { refreshToken: refreshToken }
    );
    return response.data as UsuarioResponse;
  } catch {
    return null;
  }
}

async function refreshAndRetry(error: AxiosError): Promise<AxiosResponse> {
  const isInitiator = !refreshPromise;
  if (!refreshPromise) {
    refreshPromise = doRefreshToken();
  }

  const currentPromise = refreshPromise;
  let usuario: UsuarioResponse | null;
  try {
    usuario = await currentPromise;
  } finally {
    if (isInitiator) {
      refreshPromise = null;
    }
  }

  if (usuario != null && usuario.Token) {
    if (!usuario.TokenAES) {
      usuario.TokenAES = _UserData.Get<string>(UserDataBaseEnum.TokenAES) ?? undefined;
    }
    _UserData.Register(usuario);

    const config = error.config!;
    config.headers.Authorization = `Bearer ${usuario.Token}`;
    return axios(config);
  }

  StoreAplication.dispatch({ payload: false, type: LOGIN_TOKEN });
  return Promise.reject(error);
}

axios.interceptors.response.use(
  function onFulfilled(response: AxiosResponse) {
    return response;
  },
  async function onRejected(error: AxiosError) {
    if (
      error.response?.status === 401
      && error.response.data === ""
      && !isRefreshEndpoint(error.config?.url)
    ) {
      return refreshAndRetry(error);
    }
    return Promise.reject(error);
  }
);

function GenerateErrorMessage(error: AxiosError): string {
  let msgError: string = "";
  try {

    const isValidation = isValidationResult(error.response?.data); //Verifica se data é um validation Result

    msgError = ((error.response?.data == null || error.response.data == '') ? //Se o Response do Erro for vazio, retorne a mensagem do erro base
      error.message : //Erro Bruto 
      isValidation ? (error.response?.data as ValidationResult).Message : // Se for um validation result, retorne a mensagem      
        typeof error.response?.data === "string" ? error.response?.data : //se for uma string ou objeto
          IsDebug() ? JSON.stringify(error.response?.data) : "Erro não definido!" // se for debug mostre o objeto
    ) as string
  }
  catch (error) {
    msgError = "Undefined error!"
  }

  return msgError;
}

function GenerateError(error: AxiosError | string): Error {
  if (typeof error === 'string' || error instanceof String) {
    return new Error(error as string)
  } else {
    return new Error(GenerateErrorMessage(error))
  }
}

async function CheckNetworkAccess(): Promise<void> {
  const axioError = new AxiosError("Conexão não aceita", AxiosError.ERR_NETWORK, undefined, undefined, {
    data: null,
    status: 403,
    statusText: "Proibido",
    headers: {},
    config: {},
  } as AxiosResponse);
  if (IsStorybook() && await ToastAwait("O Sistema quer acessar a rede, clique para continuar") == false) {
    throw axioError;
  }
  if (IsTest()) {
    throw axioError;
  }
}


async function CheckDevSimulate(url: string, dispatchName?: string): Promise<boolean> {
  if (IsDevSimulate()) {
    const cleanUrl = url.split("?")[0];
    const displayName = dispatchName || cleanUrl.split("/").pop();
    if (await ToastAwait(`Simular erro de ${displayName}`)) {
      return true
    }
  }
  return false
}

export async function Get<Output>(url: string, dispatchName?: string): Promise<Output | Error> {
  try {
    await CheckNetworkAccess(); // Added await here
    const response = await axios.get(url, { headers: { Authorization: GetStorageToken(), "Simulate-Error": await CheckDevSimulate(url, dispatchName) } });
    return response.data as unknown as Output;
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(dispatchName))
    return errorResult;
  }
}

export interface StreamMessage<Output> {
  eventName: string,
  data: Output
  timeStamp: Date
  messageType: MessageType
}

/* eslint-disable no-unused-vars */
export enum MessageType {
  Begin = 0,
  Running = 1,
  Success = 2,
  Error = 3
}
/* eslint-enable no-unused-vars */

export interface SSEOptions<SSEOutput> {
  // eslint-disable-next-line no-unused-vars
  onMessage?: (data: StreamMessage<SSEOutput>) => void;
  // eslint-disable-next-line no-unused-vars
  onError?: (error: Event) => void;
  onOpen?: () => void;
  dispatchName?: string;
  withCredentials?: boolean;
}

// Output Tipo do retorno Response & SSEOutput Tipo do retorno do SSE
export async function GetSSE<Output, SSEOutput = Output>(
  url: string,
  dispatchName?: string,
  options?: SSEOptions<SSEOutput>,
): Promise<Output | Error> {
  try {
    await CheckNetworkAccess();

    if (await CheckDevSimulate(url, dispatchName)) {
      throw new Error('Simulated SSE error');
    }

    const headers: Record<string, string> = {};
    const token = GetStorageToken();

    if (token) {
      headers['Authorization'] = token;
    }

    return new Promise<Output | Error>((resolve, reject) => {
      let finalResult: Output;

      const eventSource = new EventSource(url, {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: {
              ...init?.headers,
              ...headers,
            },
          }),
        withCredentials: options?.withCredentials || false,
      });

      eventSource.onopen = () => {
        options?.onOpen?.();
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as StreamMessage<SSEOutput>;

          //Dispara o Dispatch configurado
          if (options?.dispatchName != undefined && (data?.messageType != MessageType.Error && data?.messageType != MessageType.Success)) {
            StoreAplication.dispatch({
              payload: data.data,
              type: options.dispatchName
            });
          }

          options?.onMessage?.(data);

          //Ao Finalizar
          if (data?.messageType === MessageType.Success || data?.messageType === MessageType.Error) {
            finalResult = data.data as unknown as Output;
            eventSource.close();
            if (data?.messageType === MessageType.Success) {//Sucesso
              resolve(finalResult);
            } else { //Emula um Erro
              const msg = typeof data.data === "string" ? data.data : "SSE Error";
              const axioError = new AxiosError(msg, AxiosError.ERR_NETWORK, undefined, undefined, {
                data: null,
                status: 500,
                statusText: finalResult as unknown as string,
                headers: {},
                config: {},
              } as AxiosResponse);

              const errorResult = GenerateError(axioError as AxiosError);
              DispatchError(errorResult, (axioError as AxiosError).response?.status, GetDispatchLoading(dispatchName))
              resolve(errorResult);
            }

          }
        } catch (error) {
          options?.onMessage?.(event.data);
        }
      };

      eventSource.onerror = (event) => {
        options?.onError?.(event);

        if (!options?.onError) {
          eventSource.close();
          const errorObj = new Error('SSE Error');
          DispatchError(
            errorObj,
            undefined,
            GetDispatchLoading(options?.dispatchName)
          );
          reject(errorObj);
        }
      };
    });
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(options?.dispatchName));
    return errorResult;
  }
}

export async function CustomGet<Output>(url: string, config: AxiosRequestConfig, dispatchName?: string): Promise<Output | Error> {
  try {
    await CheckNetworkAccess(); // Added await here
    const response = await axios.get(url, { ...config, headers: { Authorization: config.headers?.Authorization == null ? GetStorageToken() : config.headers?.Authorization, "Simulate-Error": await CheckDevSimulate(url, dispatchName) } });
    return response.data as unknown as Output;
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(dispatchName));
    return errorResult;
  }
}

export async function Post<Input, Output>(url: string, corpo: Input, dispatchName?: string): Promise<Output | Error> {
  try {
    await CheckNetworkAccess(); // Added await here
    const response = await axios.post(url, corpo, { headers: { Authorization: GetStorageToken(), "Simulate-Error": await CheckDevSimulate(url, dispatchName) } });
    if (response.status == 200) {
      return response.data as unknown as Output;
    }
    else {
      const errorResult = GenerateError(response.statusText);
      DispatchError(errorResult, response.status, GetDispatchLoading(dispatchName))
      return errorResult;
    }
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(dispatchName))
    return errorResult;
  }
}

export async function Put<Input, Output>(url: string, corpo: Input, dispatchName?: string): Promise<Output | Error> {
  try {
    await CheckNetworkAccess(); // Added await here
    const response = await axios.put(url, corpo, { headers: { Authorization: GetStorageToken(), "Simulate-Error": await CheckDevSimulate(url, dispatchName) } });
    if (response.status == 200) {
      return response.data as unknown as Output;
    }
    else {
      const errorResult = GenerateError(response.statusText);
      DispatchError(errorResult, response.status, GetDispatchLoading(dispatchName))
      return errorResult;
    }
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(dispatchName))
    return errorResult;
  }
}

export async function Delete<Output>(url: string, dispatchName?: string): Promise<Output | Error> {
  try {
    await CheckNetworkAccess();
    const response = await axios.delete(url, { headers: { Authorization: GetStorageToken(), "Simulate-Error": await CheckDevSimulate(url, dispatchName) } });
    if (response.status == 200 || response.status == 204) {
      return response.data as unknown as Output;
    }
    else {
      const errorResult = GenerateError(response.statusText);
      DispatchError(errorResult, response.status, GetDispatchLoading(dispatchName))
      return errorResult;
    }
  }
  catch (error) {
    const errorResult = GenerateError(error as AxiosError);
    DispatchError(errorResult, (error as AxiosError).response?.status, GetDispatchLoading(dispatchName))
    return errorResult;
  }
}

function GetDispatchLoading(dispatchName?: string): StateMensage | undefined {
  const states = StoreAplication.getState();

  if (dispatchName) {
    try {
      const currentLoading: StateMensage | undefined = states.BaseReducer.Loading.find((loading: StateMensage) => loading.Name == dispatchName)
      return currentLoading
    } catch {
      return undefined
    }
  } else {
    const ultimoElemento: StateMensage | undefined = states && states.BaseReducer && states.BaseReducer.Loading && states.BaseReducer.Loading.length != 0 && states.BaseReducer.Loading[states.BaseReducer.Loading.length - 1];
    return ultimoElemento
  }
}

async function DispatchError(resultError: Error, code: number | undefined, dispatchLoading?: StateMensage) {
  StoreAplication.dispatch(DispatchLoading(NETWORK, DispatchType.ERROR, code?.toString(), StateType.Local))

  if (dispatchLoading) {
    //Executa o erro
    StoreAplication.dispatch(DispatchLoading(dispatchLoading.Name, DispatchType.ERROR, resultError.message, dispatchLoading.Type, dispatchLoading.CallBack))

    //Para o Loading
    StoreAplication.dispatch(
      {
        payload: dispatchLoading.Name,
        type: LOADING_REMOVE
      })
  }
}